import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { RouterLink } from '../../utils/linkNav';
import {
  Box, Typography, Paper, Grid, Stack, Button, IconButton, Chip, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress,
  Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip,
} from '@mui/material';
import {
  ArrowBack, ContentCopy, CheckCircle, WarningAmber, Send, ThumbUp, ThumbDown,
  VerifiedUser, Undo, Print, OpenInNew, ReceiptLong,
  AccountTree, HistoryToggleOff, Link as LinkIcon,
} from '@mui/icons-material';
import { journalWorkspaceService } from '../../services/journalWorkspaceService';
import { formatCurrency, formatAmount } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';
import { ErrorState } from '../../components/common/pageKit';
import AttachmentsPanel from '../../components/common/AttachmentsPanel';

const dt = (v?: string | null) => (v ? new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const d = (v?: string | null) => (v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

// A journal is "reversed" when it points at a counter entry — the original
// deliberately stays POSTED in the ledger (see Finance.reverseJournalEntry).
const derivedState = (h: any): string => {
  if (h?.reversal_entry_id) return 'REVERSED';
  return h?.status || 'DRAFT';
};

const STAGE_META: Record<string, { label: string; icon: React.ReactNode }> = {
  created: { label: 'Created', icon: <ReceiptLong sx={{ fontSize: 16 }} /> },
  submitted: { label: 'Submitted', icon: <Send sx={{ fontSize: 16 }} /> },
  approved: { label: 'Approved', icon: <ThumbUp sx={{ fontSize: 16 }} /> },
  rejected: { label: 'Rejected', icon: <ThumbDown sx={{ fontSize: 16 }} /> },
  posted: { label: 'Posted', icon: <VerifiedUser sx={{ fontSize: 16 }} /> },
  reversed: { label: 'Reversed', icon: <Undo sx={{ fontSize: 16 }} /> },
};

const Journal360Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const c = useControlRoomColors();

  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reason, setReason] = useState('');

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    journalWorkspaceService.detail(Number(id))
      .then((r) => { setData(r); setError(null); })
      .catch((e) => setError(e.message || 'Failed to load journal'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const stateColor = useMemo(() => ({
    DRAFT: c.sub, SUBMITTED: c.cyan, PENDING_APPROVAL: c.warn, APPROVED: c.accent,
    REJECTED: c.bad, POSTED: c.good, REVERSED: c.purple,
  } as Record<string, string>), [c]);

  const runAction = async (fn: () => Promise<any>, msg: string) => {
    setBusy(true);
    try { await fn(); setToast(msg); load(); }
    catch (e: any) { setToast(e.message || 'Action failed'); }
    finally { setBusy(false); }
  };

  if (loading) return <Box sx={{ display: 'grid', placeItems: 'center', height: 400 }}><CircularProgress /></Box>;
  if (error || !data) return <Box sx={{ p: 3 }}><ErrorState message={error || undefined} onRetry={load} /></Box>;

  const h = data.header;
  const state = derivedState(h);
  const sc = stateColor[state] || c.sub;
  const de = data.doubleEntry;
  const line = `1px solid ${c.line}`;
  const panelSx = { borderRadius: 3, border: line, bgcolor: c.panel } as const;

  const copyNumber = () => { navigator.clipboard?.writeText(h.entry_number).then(() => setToast(`Copied ${h.entry_number}`)).catch(() => {}); };

  // contextual actions by lifecycle state
  const actions: React.ReactNode[] = [];
  if (state === 'DRAFT') {
    actions.push(<Button key="submit" variant="outlined" startIcon={<Send />} disabled={busy} onClick={() => runAction(() => journalWorkspaceService.submit(h.id), 'Submitted for approval')}>Submit</Button>);
    actions.push(<Button key="post" variant="contained" startIcon={<VerifiedUser />} disabled={busy} onClick={() => runAction(() => journalWorkspaceService.post(h.id), 'Posted')}>Post</Button>);
  } else if (state === 'SUBMITTED' || state === 'PENDING_APPROVAL') {
    actions.push(<Button key="reject" color="error" variant="outlined" startIcon={<ThumbDown />} disabled={busy} onClick={() => setRejectOpen(true)}>Reject</Button>);
    actions.push(<Button key="approve" variant="contained" startIcon={<ThumbUp />} disabled={busy} onClick={() => runAction(() => journalWorkspaceService.approve(h.id), 'Approved')}>Approve</Button>);
  } else if (state === 'APPROVED') {
    actions.push(<Button key="post" variant="contained" startIcon={<VerifiedUser />} disabled={busy} onClick={() => runAction(() => journalWorkspaceService.post(h.id), 'Posted')}>Post</Button>);
  } else if (state === 'POSTED') {
    actions.push(<Button key="print" variant="outlined" startIcon={<Print />} onClick={() => window.print()}>Print</Button>);
    actions.push(<Button key="reverse" color="warning" variant="contained" startIcon={<Undo />} disabled={busy} onClick={() => setReverseOpen(true)}>Reverse</Button>);
  } else if (state === 'REVERSED' && data.related?.reversalEntry) {
    actions.push(<Button key="viewrev" variant="outlined" startIcon={<OpenInNew />} component={RouterLink} to={`/finance/journal/${data.related.reversalEntry.id}`}>View Reversal</Button>);
  }

  const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: c.sub }}>{children}</Typography>
  );
  const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <Box><Label>{label}</Label><Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>{value ?? '—'}</Typography></Box>
  );

  return (
    <Box sx={{ bgcolor: c.bg, minHeight: '100%', pb: 6 }}>
      {/* ---------------------------------------------------------- header */}
      {/* Sticky offset = AppBar height (56/64) minus main's p:3 (24px) top
          padding, which Chrome adds to a sticky's parked position — otherwise
          content bleeds through a 24px gap under the fixed AppBar. */}
      <Box sx={{ position: 'sticky', top: { xs: 32, sm: 40 }, zIndex: (t) => t.zIndex.appBar - 1, backgroundColor: 'background.paper', borderBottom: line, px: 3, py: 1.75, boxShadow: '0 4px 12px rgba(2,6,23,0.05)' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <IconButton size="small" onClick={() => navigate('/finance/journal')} sx={{ border: line }}><ArrowBack fontSize="small" /></IconButton>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', fontFamily: 'monospace' }}>{h.entry_number}</Typography>
                <Tooltip title="Copy journal number"><IconButton size="small" onClick={copyNumber}><ContentCopy sx={{ fontSize: 15 }} /></IconButton></Tooltip>
                <Chip label={state.replace('_', ' ')} size="small" sx={{ height: 22, fontWeight: 700, fontSize: '0.6875rem', bgcolor: `${sc}1f`, color: sc }} />
                {h.journal_type && <Chip label={h.journal_type} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.6875rem', borderColor: c.line }} />}
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{h.description}</Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">{actions}</Stack>
        </Stack>
      </Box>

      <Box sx={{ px: 3, pt: 2.5 }}>
        <AttachmentsPanel entityType="journal_entry" entityId={Number(id)} title="Supporting Documents" />
      </Box>

      <Box sx={{ px: 3, pt: 2.5 }}>
        <Grid container spacing={2.5}>
          {/* ==================================================== left column */}
          <Grid size={{ xs: 12, lg: 8 }}>
            {/* double-entry visualization */}
            <Paper elevation={0} sx={{ ...panelSx, p: 2.5, mb: 2.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Double-Entry</Typography>
                {de.balanced
                  ? <Chip icon={<CheckCircle sx={{ fontSize: '16px !important' }} />} label="Balanced" size="small" sx={{ fontWeight: 700, bgcolor: `${c.good}1f`, color: c.good }} />
                  : <Chip icon={<WarningAmber sx={{ fontSize: '16px !important' }} />} label="Unbalanced" size="small" sx={{ fontWeight: 700, bgcolor: `${c.bad}1f`, color: c.bad }} />}
              </Stack>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ borderRadius: 2, border: line, overflow: 'hidden' }}>
                    <Box sx={{ px: 1.5, py: 1, bgcolor: `${c.good}12`, borderBottom: line }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.06em', color: c.good }}>DEBIT</Typography>
                    </Box>
                    {de.debits.map((x: any, i: number) => (
                      <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.5, py: 1, borderBottom: i < de.debits.length - 1 ? line : 0 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Box component="span" sx={{ fontFamily: 'monospace', color: c.accent, mr: 0.75 }}>{x.account_code}</Box>{x.account_name}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', ml: 1 }}>{formatAmount(x.amount)}</Typography>
                      </Stack>
                    ))}
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 1.5, py: 1, bgcolor: c.panel2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: c.sub }}>Total Debit</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(de.totalDebit)}</Typography>
                    </Stack>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ borderRadius: 2, border: line, overflow: 'hidden' }}>
                    <Box sx={{ px: 1.5, py: 1, bgcolor: `${c.bad}12`, borderBottom: line }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.06em', color: c.bad }}>CREDIT</Typography>
                    </Box>
                    {de.credits.map((x: any, i: number) => (
                      <Stack key={i} direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 1.5, py: 1, borderBottom: i < de.credits.length - 1 ? line : 0 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <Box component="span" sx={{ fontFamily: 'monospace', color: c.accent, mr: 0.75 }}>{x.account_code}</Box>{x.account_name}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', ml: 1 }}>{formatAmount(x.amount)}</Typography>
                      </Stack>
                    ))}
                    <Stack direction="row" justifyContent="space-between" sx={{ px: 1.5, py: 1, bgcolor: c.panel2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: c.sub }}>Total Credit</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(de.totalCredit)}</Typography>
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* tabbed detail */}
            <Paper elevation={0} sx={{ ...panelSx, overflow: 'hidden' }}>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: line, px: 1, minHeight: 44, '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 600 } }}>
                <Tab label="Lines" />
                <Tab label="Source" />
                <Tab label="Approval" />
                <Tab label="Audit Trail" />
                <Tab label="Related" />
              </Tabs>

              {/* --- Lines --- */}
              {tab === 0 && (
                <TableContainer>
                  <Table size="small" sx={{ '& td, & th': { borderColor: c.line } }}>
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: c.sub, bgcolor: c.panel2 } }}>
                        <TableCell>Account</TableCell>
                        <TableCell>Dimensions</TableCell>
                        <TableCell align="right">Debit</TableCell>
                        <TableCell align="right">Credit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.lines.map((l: any) => (
                        <TableRow key={l.id}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              <Box component="span" sx={{ fontFamily: 'monospace', color: c.accent, mr: 0.75 }}>{l.account_code}</Box>{l.account_name}
                            </Typography>
                            {l.narration && <Typography variant="caption" color="text.secondary">{l.narration}</Typography>}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {l.cost_center_name && <Chip size="small" label={`CC: ${l.cost_center_name}`} variant="outlined" sx={{ height: 20, fontSize: '0.625rem', borderColor: c.line }} />}
                              {l.profit_center_name && <Chip size="small" label={`PC: ${l.profit_center_name}`} variant="outlined" sx={{ height: 20, fontSize: '0.625rem', borderColor: c.line }} />}
                              {l.project_name && <Chip size="small" label={l.project_name} variant="outlined" sx={{ height: 20, fontSize: '0.625rem', borderColor: c.line }} />}
                              {l.tax_code && <Chip size="small" label={l.tax_code} variant="outlined" sx={{ height: 20, fontSize: '0.625rem', borderColor: c.line }} />}
                              {l.vendor_name && <Chip size="small" label={l.vendor_name} variant="outlined" sx={{ height: 20, fontSize: '0.625rem', borderColor: c.line }} />}
                              {l.customer_name && <Chip size="small" label={l.customer_name} variant="outlined" sx={{ height: 20, fontSize: '0.625rem', borderColor: c.line }} />}
                              {!l.cost_center_name && !l.profit_center_name && !l.project_name && !l.tax_code && !l.vendor_name && !l.customer_name && <Typography variant="caption" color="text.secondary">—</Typography>}
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{l.debit_amount > 0 ? formatCurrency(l.debit_amount) : '—'}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{l.credit_amount > 0 ? formatCurrency(l.credit_amount) : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* --- Source --- */}
              {tab === 1 && (
                <Box sx={{ p: 2.5 }}>
                  {data.source ? (
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: `${c.accent}14` }}><AccountTree sx={{ color: c.accent }} /></Box>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{data.source.number || data.source.type}</Typography>
                          <Typography variant="caption" color="text.secondary">System-generated from {String(data.source.type).replace(/_/g, ' ').toLowerCase()}</Typography>
                        </Box>
                        <Box sx={{ flex: 1 }} />
                        {data.source.route && <Button size="small" variant="outlined" endIcon={<OpenInNew />} component={RouterLink} to={data.source.route}>Open origin</Button>}
                      </Stack>
                      {data.source.fields && Object.keys(data.source.fields).length > 0 && (
                        <Grid container spacing={2} sx={{ pt: 1 }}>
                          {Object.entries(data.source.fields).map(([k, v]) => (
                            <Grid size={{ xs: 6, sm: 4 }} key={k}>
                              <Field label={k} value={typeof v === 'number' ? formatCurrency(v as number) : (v as any ?? '—')} />
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Stack>
                  ) : (
                    <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
                      <ReceiptLong sx={{ fontSize: 32, color: c.sub }} />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Manual journal</Typography>
                      <Typography variant="caption" color="text.secondary">This entry was created manually — it has no originating transaction.</Typography>
                    </Stack>
                  )}
                </Box>
              )}

              {/* --- Approval --- */}
              {tab === 2 && (
                <Box sx={{ p: 2.5 }}>
                  <Grid container spacing={2.5}>
                    <Grid size={{ xs: 12, sm: 6 }}><Field label="Submitted by" value={data.audit.submittedBy?.name} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><Field label="Submitted at" value={dt(data.audit.submittedAt)} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><Field label="Approved by" value={data.audit.approvedBy?.name} /></Grid>
                    <Grid size={{ xs: 12, sm: 6 }}><Field label="Approved at" value={dt(data.audit.approvedAt)} /></Grid>
                    {data.audit.rejectedAt && <>
                      <Grid size={{ xs: 12, sm: 6 }}><Field label="Rejected by" value={data.audit.rejectedBy?.name} /></Grid>
                      <Grid size={{ xs: 12, sm: 6 }}><Field label="Rejected at" value={dt(data.audit.rejectedAt)} /></Grid>
                      <Grid size={{ xs: 12 }}><Field label="Rejection reason" value={data.audit.rejectionReason} /></Grid>
                    </>}
                    {!data.audit.submittedAt && !data.audit.approvedAt && !data.audit.rejectedAt && (
                      <Grid size={{ xs: 12 }}><Typography variant="body2" color="text.secondary">This journal did not go through an approval workflow.</Typography></Grid>
                    )}
                  </Grid>
                </Box>
              )}

              {/* --- Audit trail --- */}
              {tab === 3 && (
                <Box sx={{ p: 2.5 }}>
                  <Stack spacing={0}>
                    {[
                      { l: 'Created', by: data.audit.createdBy?.name, at: data.audit.createdAt },
                      { l: 'Submitted', by: data.audit.submittedBy?.name, at: data.audit.submittedAt },
                      { l: 'Approved', by: data.audit.approvedBy?.name, at: data.audit.approvedAt },
                      { l: 'Rejected', by: data.audit.rejectedBy?.name, at: data.audit.rejectedAt },
                      { l: 'Posted', by: data.audit.postedBy?.name, at: data.audit.postedAt },
                      { l: 'Reversed', by: data.audit.reversedBy?.name, at: data.audit.reversedAt },
                    ].filter((x) => x.at).map((x, i) => (
                      <Stack key={i} direction="row" spacing={2} sx={{ py: 1.25, borderBottom: i < 5 ? line : 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, width: 110 }}>{x.l}</Typography>
                        <Typography variant="body2" sx={{ flex: 1 }}>{x.by || '—'}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>{dt(x.at)}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* --- Related --- */}
              {tab === 4 && (
                <Box sx={{ p: 2.5 }}>
                  <Stack spacing={1.25}>
                    {data.related.reversalOf && <RelatedRow c={c} label="Reverses" entry={data.related.reversalOf} to={`/finance/journal/${data.related.reversalOf.id}`} />}
                    {data.related.reversalEntry && <RelatedRow c={c} label="Reversed by" entry={data.related.reversalEntry} to={`/finance/journal/${data.related.reversalEntry.id}`} />}
                    {(data.related.sameSource || []).map((s: any) => (
                      <RelatedRow key={s.id} c={c} label="Same source" entry={{ entry_number: s.entryNumber, ...s }} sub={s.description} to={`/finance/journal/${s.id}`} />
                    ))}
                    {!data.related.reversalOf && !data.related.reversalEntry && (data.related.sameSource || []).length === 0 && (
                      <Typography variant="body2" color="text.secondary">No related journal entries.</Typography>
                    )}
                  </Stack>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* ==================================================== right rail */}
          <Grid size={{ xs: 12, lg: 4 }}>
            {/* summary */}
            <Paper elevation={0} sx={{ ...panelSx, p: 2.5, mb: 2.5 }}>
              <Grid container spacing={2}>
                <Grid size={6}><Field label="Entry Date" value={d(h.entry_date)} /></Grid>
                <Grid size={6}><Field label="Posting Date" value={d(h.posting_date || h.entry_date)} /></Grid>
                <Grid size={6}><Field label="Company" value={h.company} /></Grid>
                <Grid size={6}><Field label="Plant" value={h.plant} /></Grid>
                <Grid size={6}><Field label="Reference" value={h.reference} /></Grid>
                <Grid size={6}><Field label="Currency" value={`${h.currency || 'INR'} @ ${h.exchange_rate || 1}`} /></Grid>
                <Grid size={6}><Field label="Cost Center" value={h.cost_center_name} /></Grid>
                <Grid size={6}><Field label="Project" value={h.project_name} /></Grid>
                <Grid size={6}><Field label="Financial Year" value={h.financial_year} /></Grid>
                <Grid size={6}><Field label="Source" value={h.source_type ? String(h.source_type).replace(/_/g, ' ') : 'Manual'} /></Grid>
              </Grid>
            </Paper>

            {/* lifecycle timeline */}
            <Paper elevation={0} sx={{ ...panelSx, p: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <HistoryToggleOff sx={{ fontSize: 20, color: c.accent }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>Lifecycle</Typography>
              </Stack>
              <Stack spacing={0}>
                {data.lifecycle.map((ev: any, i: number) => {
                  const meta = STAGE_META[ev.stage] || { label: ev.stage, icon: <ReceiptLong sx={{ fontSize: 16 }} /> };
                  const isRejected = ev.stage === 'rejected';
                  const tint = isRejected ? c.bad : c.good;
                  const last = i === data.lifecycle.length - 1;
                  return (
                    <Stack key={i} direction="row" spacing={1.5}>
                      <Stack alignItems="center">
                        <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: `${tint}1a`, color: tint, flexShrink: 0 }}>{meta.icon}</Box>
                        {!last && <Box sx={{ width: 2, flex: 1, minHeight: 22, bgcolor: c.line, my: 0.25 }} />}
                      </Stack>
                      <Box sx={{ pb: last ? 0 : 1.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{meta.label}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{ev.byName || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>{dt(ev.at)}</Typography>
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* reject dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Reject Journal</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth size="small" autoFocus multiline minRows={2} label="Reason for rejection" value={reason} onChange={(e) => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={!reason.trim() || busy}
            onClick={() => { setRejectOpen(false); runAction(() => journalWorkspaceService.reject(h.id, reason), 'Journal rejected'); setReason(''); }}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* reverse dialog */}
      <Dialog open={reverseOpen} onClose={() => setReverseOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>Reverse Journal</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A balanced counter-entry will be posted to cancel {h.entry_number}. The original stays posted for audit; both net to zero in the ledger.
          </Typography>
          <TextField fullWidth size="small" autoFocus label="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReverseOpen(false)}>Cancel</Button>
          <Button color="warning" variant="contained" disabled={busy}
            onClick={() => { setReverseOpen(false); runAction(() => journalWorkspaceService.reverse(h.id, reason || undefined), 'Journal reversed with counter-entry'); setReason(''); }}>Reverse</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={2800} onClose={() => setToast(null)} message={toast || ''} />
    </Box>
  );
};

const RelatedRow: React.FC<{ c: any; label: string; entry: any; sub?: string; to: string }> = ({ c, label, entry, sub, to }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} component={RouterLink} to={to}
    sx={{ p: 1.25, borderRadius: 2, border: `1px solid ${c.line}`, cursor: 'pointer', textDecoration: 'none', color: 'inherit', '&:hover': { bgcolor: c.panel2 } }}>
    <LinkIcon sx={{ fontSize: 18, color: c.sub }} />
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: c.sub, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '0.625rem' }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{entry.entry_number}</Typography>
      {sub && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</Typography>}
    </Box>
    <OpenInNew sx={{ fontSize: 16, color: c.sub }} />
  </Stack>
);

export default Journal360Page;
